import React, {memo, useCallback, useEffect, useMemo, useState} from "react"
import {PasswordContainer} from "../../../../components/PasswordContainer"
import {SettingsPageContainer} from "../../../../components/Settings/SettingsPageContainer"
import {useHistory, useLocation, useParams} from "react-router-dom"
import {useQuestion} from "../../../../hooks/question"
import {useMessage} from "../../../../hooks/message"
import {useRPC} from "../../../../hooks/rpc"
import {useKeyboard} from "../../../../hooks/keyboard"
import {SettingInputComponent} from "../../../../components/Settings/SettingInputComponent"
import {useSelector} from "react-redux"
import {
    CONTROL_TYPE_ID_ALARM,
    CONTROL_TYPE_ADV_THERMOSTAT,
    InputModel,
    InputTypeModel,
} from "../../../../types/control"
import {SelectItem} from "../../../../components/Select"
import {BridgeDeviceModel} from "../../../../types/bridge-device"
import {useSetSelector} from "../../../../hooks/set-selector"
import {useMap} from "../../../../hooks/map"
import {useFilterSelector} from "../../../../hooks/filter-selector"
import {useLocalization} from "../../../../hooks/localization"
import {SettingReadOnlyRowComponent} from "../../../../components/Settings/SettingReadOnlyRowComponent"

const BACK_URL = "/settings/multiple-temperature"

type CONTROL_VALUE = {
    id: string
    isVirtual: boolean
}
export const MultipleTemperatureEditScreen = memo(function MultipleTemperatureEditScreen() {
    const {t} = useLocalization()
    const params = useParams<{id: string}>()
    const question = useQuestion()
    const {success, failure} = useMessage()
    const [rpc] = useRPC()
    const history = useHistory()
    const location = useLocation<{controlId: string; bridgeDeviceId: string; inputTypeId: string; input: InputModel}>()
    const previousLocationSelectedControl = useSelector(state =>
        state.control.controlList.find(c => c.id === location?.state?.controlId)
    )
    const [setActiveInput, renderKeyboard, currentInput] = useKeyboard()

    const inputList = useSelector(state => state.control.inputList)
    const bridgeDeviceTypeSet = useSetSelector(state => state.control.bridgeDeviceTypeList)
    const bridgeDeviceList = useSelector(state => state.control.bridgeDeviceList)
    const bridgeDeviceOptionList: SelectItem<BridgeDeviceModel>[] = useMemo(
        () =>
            bridgeDeviceList
                .filter(bridgeDevice => bridgeDevice.type_id === "20")
                .map(bridgeDevice => ({
                    value: bridgeDevice,
                    label: bridgeDevice.name
                })),
        [bridgeDeviceList]
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const selected = useMemo(
        () => location.state?.input || inputList.find(control => control.id === params?.id) || null,
        []
    )
    const [bridgeDevice, setBridgeDevice] = useState<SelectItem<BridgeDeviceModel>>(
        selected == null
            ? location?.state?.bridgeDeviceId == null
                ? null
                : bridgeDeviceOptionList.find(
                      bridgeDeviceOption => bridgeDeviceOption.value.id === location?.state?.bridgeDeviceId
                  ) || null
            : bridgeDeviceOptionList.find(
                  bridgeDeviceOption => bridgeDeviceOption.value.id === selected.bridge_device_id
              ) || null
    )
    const bridgeDeviceType = useMemo(
        () => (bridgeDevice == null ? null : bridgeDeviceTypeSet[bridgeDevice.value.type_id]),
        [bridgeDevice, bridgeDeviceTypeSet]
    )
    const inputTypeList = useSelector(state => state.control.inputTypeList)
    const inputTypeOptionList: SelectItem<InputTypeModel>[] = useMemo(
        () =>
            inputTypeList
                .filter(inputList => inputList.id === "6")
                .map(inputType => ({
                    value: inputType,
                    label: inputType.name
                })),
        [inputTypeList]
    )
    const [inputType, setInputType] = useState<SelectItem<InputTypeModel>>(
        selected == null
            ? location?.state?.inputTypeId == null
                ? null
                : inputTypeOptionList.find(
                      inputTypeOption => inputTypeOption.value.id === location.state.inputTypeId
                  ) || null
            : inputTypeOptionList.find(inputTypeOption => inputTypeOption.value.id === selected.type_id) || null
    )
    const bridgeDeviceFilteredControlOptionList: SelectItem<CONTROL_VALUE>[] = useMap(
        useFilterSelector(
            state => state.control.controlList,
            control =>
                control.bridge_device_id !== bridgeDevice?.value.id && control.type_id === CONTROL_TYPE_ADV_THERMOSTAT,
            [bridgeDevice?.value]
        ),
        controlModel => ({label: controlModel.name, value: {isVirtual: false, id: controlModel.id}}),
        []
    )

    const controlOptionList: SelectItem<CONTROL_VALUE>[] = useMemo(
        () => [...bridgeDeviceFilteredControlOptionList],
        [bridgeDeviceFilteredControlOptionList]
    )
    const inputNumberOptionList: SelectItem<number>[] = useMemo(() => {
        const inputCount = bridgeDeviceType?.input_count || 0
        if (inputCount === 0) {
            return []
        }

        const list = []
        for (let i = 0; i < inputCount; i++) {
            list.push({value: i, label: t("entrance") + " " + i.toString()})
        }

        return list
    }, [t, bridgeDeviceType])

    const [name, setName] = useState((selected?.id != null && selected?.name) || "")
    const [controlOption, setControlOption] = useState<SelectItem<CONTROL_VALUE>>(
        selected == null
            ? null
            : bridgeDeviceFilteredControlOptionList.find(option => option.value.id === selected?.relational_id) || null
    )

    const [inputNumber, setInputNumber] = useState<SelectItem<number>>(
        selected == null ? null : inputNumberOptionList.find(option => option?.value === selected?.input_number) || null
    )

    const findInputNumber = useMemo(
        () => ({value: selected?.input_number, label: t("entrance") + " " + selected?.input_number?.toString()}),
        [t, selected]
    )


    const onBackPressed = useCallback(() => {
        history.push(BACK_URL)
    }, [history])

    const onDeletePressed = useCallback(() => {
        question(t("are_you_sure_to_delete_the_input"), () => {
            return rpc
                .deleteInput(selected.id)
                .then(() => {
                    success(t("input_successfully_deleted"))
                    onBackPressed()
                })
                .catch(failure)
        })
    }, [t, failure, onBackPressed, question, rpc, selected?.id, success])

    const isFieldsValid = useCallback((): boolean => {
        if (name == null || name.length === 0) {
            failure(t("pls_enter_name"))

            return false
        }

        return true
    }, [t, failure, name])

    const onGoBackPressed = useCallback(() => {
        if (previousLocationSelectedControl?.id != null) {
            success(t("control_successfully_saved"))
        }

        onBackPressed()
    }, [t, previousLocationSelectedControl?.id, success, onBackPressed])

    const onCreatePressed = useCallback(() => {
        if (!isFieldsValid()) {
            return
        }

        const input: InputModel = {
            ...selected,
            id: selected?.id || null,
            name
            /*is_active: true,
            is_notification: false,
            bridge_device_id: bridgeDevice?.value?.id,
            type_id: inputType?.value.id,
            direction: inputType?.value?.id === INPUT_TYPE_ID_SCENARIO || inputType?.value?.id === INPUT_TYPE_ID_SENSOR ? 1 : direction?.value,
            is_virtual_control: selected?.is_virtual_control || false,
            relational_id: inputType?.value?.id === INPUT_TYPE_ID_SCENARIO ? null : controlOption?.value?.id,
            should_output_reverse: outputReverse,
            input_number: inputNumber?.value,
            sensor_settings: null*/
        }

        return rpc
            .updateInput(input)
            .then(() => {
                const message =
                    selected?.id != null
                        ? t("input_successfully_edited")
                        : previousLocationSelectedControl?.id == null
                        ? t("input_successfully_saved")
                        : t("control_and_input_successfully_saved")
                success(message)
                onBackPressed()
            })
            .catch(failure)
    }, [
        t,
        isFieldsValid,
        rpc,
        success,
        failure,
        selected,
        previousLocationSelectedControl?.id,
        onBackPressed,
        name
        /* bridgeDevice,
        inputType,
        direction,
        outputReverse,
        inputNumber,
        controlOption*/
    ])
    const virtualControlSetList = useSetSelector(state => state.control.virtualControlList)
    const controlSet = useSetSelector(state => state.control.controlList)

    const findControlType = useMemo(() => {
        return selected?.is_virtual_control && selected.type_id === CONTROL_TYPE_ADV_THERMOSTAT
            ? controlSet[virtualControlSetList[selected?.relational_id]?.control_id]
            : controlSet[selected?.relational_id]
    }, [virtualControlSetList, controlSet, selected])

    const buttons = useMemo(
        () => [
            {
                text: t("delete"),
                className: "mulberry-wood",
                onClick: onDeletePressed,
                hidden: !selected?.id
            },
            {
                text: t("save"),
                className: "green",
                onClick: onCreatePressed
            },
            {
                text: t("cancel"),
                className: "red",
                onClick: onGoBackPressed
            }
        ],
        [t, onCreatePressed, onDeletePressed, selected, onGoBackPressed]
    )

    const onBlur = useCallback(() => {
        setActiveInput(null)
    }, [setActiveInput])

    useEffect(() => {
        setControlOption(
            controlOptionList.find(controlOptionItem => controlOptionItem.value === controlOption?.value) || null
        )

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [controlOptionList])

    useEffect(() => {
        setInputNumber(inputNumberOptionList.find(option => option.value === inputNumber?.value) || null)

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inputNumberOptionList])

    useEffect(() => {
        if (previousLocationSelectedControl?.id != null) {
            setControlOption(
                controlOptionList.find(
                    controlOption => controlOption.value.id === previousLocationSelectedControl.id
                ) || null
            )

            const nameSuffix =
                previousLocationSelectedControl.type_id === CONTROL_TYPE_ID_ALARM ? t("its_sensor") : t("its_key")
            setName(previousLocationSelectedControl.name + nameSuffix)
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [t, previousLocationSelectedControl?.id])

    return (
        <PasswordContainer requiredRole="service" isFullscreen={true}>
            <SettingsPageContainer
                buttons={buttons}
                className="service-edit"
                backUrl={BACK_URL}
                currentInput={currentInput}
            >
                <div className="content-divider">
                    <div className="content-wrapper">
                        <SettingInputComponent
                            placeholder={t("write_input_name")}
                            label={t("input_name")}
                            value={name}
                            setValue={setName}
                            setActiveInput={setActiveInput}
                            onBlur={onBlur}
                        />
                        <SettingReadOnlyRowComponent title={t("device")} value={bridgeDevice?.label || "-"} />
                        <SettingReadOnlyRowComponent title={t("input_type")} value={inputType?.label || "-"} />
                        <SettingReadOnlyRowComponent title={t("control")} value={findControlType?.name || "-"} />
                        <SettingReadOnlyRowComponent title={t("input_no")} value={findInputNumber?.label || "-"} />
                    </div>
                    <div className="content-wrapper"></div>
                </div>
            </SettingsPageContainer>
            {renderKeyboard()}
        </PasswordContainer>
    )
})
